import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_TERMS_UPDATED_AT, LEGAL_TERMS_VERSION, PRIVACY_CONTACT_EMAIL } from '../constants/legal';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="space-y-3">
        <h2 className="text-xl font-semibold text-snapfy-text-primary">{title}</h2>
        <div className="text-snapfy-text-secondary space-y-2">{children}</div>
    </section>
);

const SectionDivider: React.FC = () => (
    <div className="py-4">
        <div className="w-16 border-t border-snapfy-border"></div>
    </div>
);

const TermsPage: React.FC = () => {
    return (
        <div className="max-w-3xl mx-auto py-8 px-4 text-snapfy-text-secondary">
            <header className="mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-snapfy-text-primary mb-2">Termos de Uso</h1>
                <p className="text-sm text-snapfy-text-secondary">
                    Última atualização: {LEGAL_TERMS_UPDATED_AT} · versão {LEGAL_TERMS_VERSION}
                </p>
            </header>

            <div className="space-y-8">
                <p className="text-snapfy-text-secondary">
                    Estes Termos de Uso regulam o uso da plataforma Snapfy.ai, oferecida por SNAPFY.AI LTDA, inscrita no CNPJ sob o nº 57.189.141/0001-11, doravante denominada simplesmente “Snapfy.ai”.
                </p>
                <p className="text-snapfy-text-secondary">
                    Para criar uma conta e usar a plataforma, o usuário precisa ler e aceitar expressamente estes Termos e a{' '}
                    <Link to="/privacy" className="text-snapfy-accent-soft hover:underline">Política de Privacidade</Link>, marcando a opção de aceite. O aceite fica registrado com a data, a versão aceita e o endereço IP.
                </p>

                <SectionDivider />

                <Section title="1. Objeto">
                    <p>
                        A Snapfy.ai é uma plataforma online que gera imagens e vídeos com inteligência artificial — como modelos virtuais vestindo produtos, provador virtual, cenas de produto e anúncios — a partir de fotos, desenhos técnicos, textos e demais conteúdos enviados pelo usuário.
                    </p>
                </Section>

                <SectionDivider />

                <Section title="2. Acesso à plataforma">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>A plataforma é destinada a maiores de 18 anos e a pessoas jurídicas, por meio de seus representantes.</li>
                        <li>O acesso se dá mediante criação de conta e adesão a um plano com liberação de créditos.</li>
                        <li>É necessário fornecer dados verdadeiros e mantê-los atualizados.</li>
                        <li>O acesso é pessoal, intransferível e protegido por senha. Em contas de empresa, o administrador da empresa gerencia os membros e pode ver o conteúdo gerado na conta da empresa.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="3. Plano gratuito (trial)">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Ao criar uma conta, o usuário recebe 10 créditos gratuitos (equivalente a 10 imagens) para testar a plataforma.</li>
                        <li>O período de teste tem duração de 7 dias corridos, com a obrigatoriedade de cadastro de cartão de crédito.</li>
                        <li>A cobrança do plano pago é iniciada automaticamente após o término do período de teste, caso o usuário não cancele dentro desse prazo.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="4. Créditos e uso">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Cada imagem gerada consome 1 crédito.</li>
                        <li>Os créditos são renovados mensalmente e acumulam caso não sejam utilizados.</li>
                        <li>Os créditos não expiram enquanto o plano estiver ativo.</li>
                        <li>Em caso de cancelamento, os créditos permanecem vinculados à conta, podendo ser utilizados após reativação do plano.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="5. Cancelamento e renovação">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>O plano é renovado automaticamente a cada ciclo (mensal).</li>
                        <li>O usuário pode cancelar a assinatura a qualquer momento, mantendo o acesso até o final do ciclo vigente.</li>
                        <li>Após o término do ciclo, o acesso à plataforma será suspenso até a reativação.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="6. Conteúdo enviado pelo usuário">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>O usuário declara ter os direitos sobre tudo o que envia (fotos, marcas, textos, links) e, quando houver pessoas identificáveis nas imagens, a autorização de cada uma delas para o uso da imagem — e, no caso de menores de idade, a autorização dos responsáveis legais.</li>
                        <li>O conteúdo enviado é usado para prestar o serviço: é armazenado de forma segura e processado pelos provedores de inteligência artificial contratados pela Snapfy.ai, como descrito na Política de Privacidade.</li>
                        <li>Os resultados gerados por inteligência artificial podem conter imprecisões. Cabe ao usuário revisar o conteúdo antes de publicá-lo e responder pelo uso que fizer dele.</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="7. Propriedade e uso das imagens geradas">
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>As imagens geradas pelo usuário podem ser utilizadas livremente por ele para fins comerciais, promocionais ou editoriais.</li>
                        <li>A Snapfy.ai não reivindica direitos autorais sobre o conteúdo gerado.</li>
                        <li>
                            A Snapfy.ai só usa imagens geradas pelo usuário em portfólio, demonstrações e divulgação se o usuário autorizar expressamente — no cadastro, no aceite destes Termos ou no seu perfil. A autorização é opcional, não condiciona o uso da plataforma e pode ser revogada a qualquer momento no perfil; a revogação vale para os usos futuros.
                        </li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="8. Restrições de uso">
                    <p>É proibido:</p>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Utilizar a plataforma para fins ilegais ou não autorizados.</li>
                        <li>Enviar conteúdos ofensivos, discriminatórios ou que violem direitos de terceiros, inclusive direitos de imagem, autorais e de marca.</li>
                        <li>Gerar conteúdo que se passe por outra pessoa, que a exponha sem autorização ou que envolva menores de idade de forma inadequada.</li>
                        <li>Tentar acessar áreas ou dados não autorizados.</li>
                        <li>Compartilhar acessos ou burlar o sistema de créditos.</li>
                    </ul>
                    <p>A Snapfy.ai pode usar verificações automáticas para bloquear gerações que violem estas regras. O usuário pode pedir a revisão de um bloqueio pelo canal de suporte.</p>
                </Section>

                <SectionDivider />

                <Section title="9. Suporte">
                    <p>
                        O suporte técnico é oferecido via e-mail ou WhatsApp, de segunda a sexta, das 9h às 18h (horário de Brasília).
                    </p>
                </Section>

                <SectionDivider />

                <Section title="10. Privacidade e proteção de dados">
                    <p>
                        A Snapfy.ai trata os dados pessoais de acordo com a Lei Geral de Proteção de Dados Pessoais – LGPD (Lei nº 13.709/2018). Os detalhes — quais dados, para quê, com base em quê, com quem são compartilhados e como exercer seus direitos — estão na{' '}
                        <Link to="/privacy" className="text-snapfy-accent-soft hover:underline">Política de Privacidade</Link>. Encarregado pelo tratamento de dados:{' '}
                        <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-snapfy-accent-soft hover:underline">{PRIVACY_CONTACT_EMAIL}</a>.
                    </p>
                </Section>

                <SectionDivider />

                <Section title="11. Alterações nos termos">
                    <p>
                        A Snapfy.ai poderá atualizar estes Termos. Mudanças relevantes serão informadas por e-mail ou na própria plataforma, e o usuário precisará aceitar a nova versão para continuar usando o serviço. Se não concordar, poderá cancelar a conta a qualquer momento.
                    </p>
                </Section>

                <SectionDivider />

                <Section title="12. Foro">
                    <p>
                        Fica eleito o foro da comarca de São Paulo/SP para dirimir eventuais conflitos decorrentes destes Termos, sem prejuízo do direito do consumidor de propor ação no foro do seu domicílio.
                    </p>
                </Section>
            </div>
        </div>
    );
};

export default TermsPage;
